import { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/by-slug/da/kontakt')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then((data) => {
        setPageData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching contact page content from CMS:', err);
        // Fallback to seeded content if API is unreachable
        setPageData({
          title: 'Kontakt',
          meta_title: 'Kontakt - Dansketelemaster Invest',
          meta_description: 'Kontakt Dansketelemaster Invest på Niels Jernes Vej 10 i Aalborg Ø. Se telefon, e-mail, CVR og bankoplysninger.',
        });
        setLoading(false);
      });
  }, []);

  const metaTitle = pageData?.meta_title || 'Kontakt - Dansketelemaster Invest';
  const metaDescription = pageData?.meta_description || 'Kontakt Dansketelemaster Invest på Niels Jernes Vej 10 i Aalborg Ø. Se telefon, e-mail, CVR og bankoplysninger.';

  const openingHours = [
    { day: 'Mandag', hours: '10:00 – 17:00' },
    { day: 'Tirsdag', hours: '10:00 – 17:00' },
    { day: 'Onsdag', hours: '10:00 – 17:00' },
    { day: 'Torsdag', hours: '10:00 – 20:00' },
    { day: 'Fredag', hours: '10:00 – 15:00' },
    { day: 'Lørdag', hours: '10:00 – 14:00' },
    { day: 'Søndag', hours: 'Lukket', closed: true },
  ];

  return (
    <div className="bg-white min-h-screen">
      <SEO title={metaTitle} description={metaDescription} />

      <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
        <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-12 border-b border-slate-100 pb-6">
          Kontakt os
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Contact info, map, opening hours */}
          <div className="lg:col-span-7 space-y-10">
            {/* Contact Details */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900">Dansketelemaster Invest</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-slate-600 text-sm md:text-base">
                <div className="space-y-2">
                  <p className="flex items-start gap-3">
                    <i className="fa-solid fa-location-dot text-[#2a4692] mt-1 w-5 text-center" />
                    <span>
                      <a
                        href="https://maps.app.goo.gl/uK69D1PZc6Z87X6u9"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-[#2a4692] transition-colors hover:underline"
                      >
                        Niels Jernes Vej 10<br />
                        9220 Aalborg Ø
                      </a>
                    </span>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="fa-solid fa-phone text-[#2a4692] w-5 text-center" />
                    <span className="whitespace-nowrap">
                      Telefon: <a href="tel:+4550358378" className="hover:text-[#2a4692] transition-colors font-medium text-slate-900">50 358 378</a>
                    </span>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="fa-solid fa-envelope text-[#2a4692] w-5 text-center" />
                    <span className="whitespace-nowrap">
                      E-mail: <a href="mailto:info@dansketelemasterinvest.dk" className="hover:text-[#2a4692] transition-colors font-medium text-slate-900">info@dansketelemasterinvest.dk</a>
                    </span>
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="flex items-center gap-3">
                    <i className="fa-solid fa-id-card text-[#2a4692] w-5 text-center" />
                    <span>CVR nr: <strong className="text-slate-900">21810797</strong></span>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="fa-solid fa-building-columns text-[#2a4692] w-5 text-center" />
                    <span>Bank: <strong className="text-slate-900">Danske Bank</strong></span>
                  </p>
                </div>
              </div>
            </div>

            {/* Map & Opening Hours Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t border-slate-100">
              {/* Map */}
              <div className="space-y-3">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <i className="fa-solid fa-map text-[#2a4692]" />
                  Find os på kortet
                </h3>
                <div className="w-full aspect-square md:aspect-auto md:h-[220px] rounded-lg overflow-hidden border border-slate-200 shadow-sm relative">
                  <iframe
                    title="Dansketelemaster Invest Aalborg"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2137.402482322345!2d9.9793133!3d57.0148533!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4649326770f5e399%3A0x6a9336b13994c92!2sNiels%20Jernes%20Vej%2010%2C%209220%20Aalborg%20%C3%98!5e0!3m2!1sda!2sdk!4v1719234903149!5m2!1sda!2sdk"
                    className="absolute inset-0 w-full h-full border-0"
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>

              {/* Opening Hours */}
              <div className="space-y-3">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <i className="fa-solid fa-clock text-[#2a4692]" />
                  Åbningstider
                </h3>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                  <ul className="space-y-2 text-sm">
                    {openingHours.map((item) => (
                      <li key={item.day} className="flex justify-between items-center border-b border-slate-200/60 pb-1.5 last:border-0 last:pb-0">
                        <span className="font-semibold text-slate-700">{item.day}</span>
                        <span className={item.closed ? 'text-red-500 font-medium' : 'text-slate-600 font-mono'}>
                          {item.hours}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-5">
            <ContactForm />
          </div>
        </div>
      </div>
    </div>
  );
}
